/**
 * The top-level route table for the login app, as data.
 *
 * This is what `components/App/App.tsx` used to express as a `<Switch>` tree.
 * `routing/AppRoutes.tsx` renders it back into `<Switch>/<Route>` with identical
 * behaviour; the Next host will generate its pages from the same table (Phase 5).
 *
 * Scope note (Phase 0): only the App.tsx `<Switch>` is table-ised here. The
 * `POST_LOGIN` entry keeps rendering `<PostLoginRouter>`, whose own nested
 * routing (and its `postLoginStore.isLoaded` gating) is collapsed into nested
 * entries in Phase 2 — see docs/ssr-shared-source-plan.md, Phase 2 item 1.
 *
 * See docs/ssr-shared-source-plan.md, section 2.2.
 */
import React from 'react';
import { ROUTES, DEFAULT_ROUTE } from './routes';
import type { RootStore } from './stores/root';

import AccountRecoveryCodeDialog from './components/AccountRecoveryCodeDialog';
import AccountVerification from './components/AccountVerification/AccountVerification';
import EmailAlreadyExist from './components/EmailAlreadyExist';
import Login from './components/Login/Login';
import SelectAccount from './components/SelectAccount/SelectAccount';
import SelectWorkspace from './components/SelectWorkspace/SelectWorkspace';
import StepUpDialog from './components/StepUpDialog';
import TwoFactorAuthDialog from './components/TwoFactorAuthDialog';
import PostLoginRouter from './components/PostLogin/PostLoginRouter';
import PostSignupEmailConfirm from './components/PostSignUp/PostSignupEmailConfirm';
import { ApproveAndContinue } from './components/ApproveAndContinue/ApproveAndContinue';
import { BlockedAccount } from './components/BlockedAccount/BlockedAccount';
import { ConnectionSuccessful } from './components/ConnectionSuccessful/ConnectionSuccessful';
import { DeviceAuthorize } from './components/DeviceAuthorize/DeviceAuthorize';
import { DeviceLogin } from './components/DeviceLogin/DeviceLogin';
import { DisconnectSuccessful } from './components/DisconnectSuccessful/DisconnectSuccessful';
import { EmailStepDialog } from './components/EmailStep/EmailStep';
import { EmailVerificationResult } from './components/EmailVerificationResult';
import { EnterEmailForSocial } from './components/EnterEmailForSocial';
import { LoadingDialog } from './components/LoadingDialog/LoadingDialog';
import { PosDeviceConnectionSuccessful } from './components/PosDeviceConnectionSuccessful';
import { PosDeviceLogin } from './components/PosDeviceLogin';
import { ResetPassword } from './components/ResetPassword/ResetPassword';
import { ResetPasswordConfirmation } from './components/ResetPasswordConfirmation/ResetPasswordConfirmation';
import { ResetPasswordInvalid } from './components/ResetPasswordInvalid/ResetPasswordInvalid';
import { Signup } from './components/Signup';
import { SocialOnlyDialog } from './components/SocialOnlyDialog/SocialOnlyDialog';
import { SsoEnterEmail } from './components/SsoEnterEmail';
import { Web5DeviceConnectionSuccessful } from './components/Web5DeviceConnectionSuccessful';
import { Web5DeviceLogin } from './components/Web5DeviceLogin';

export interface RouteEntry {
  /** Stable id, used by the smoke harness burn-down and (Phase 5) Next pages. */
  id: string;
  /** Path(s) from ROUTES; an array mirrors a react-router multi-path route. */
  path: string | string[];
  exact?: boolean;
  component: React.ComponentType<any>;
  /** Today's ProtectedRoute condition. Absent → always accessible. */
  guard?: (root: RootStore) => boolean;
  /** Today's ProtectedRoute defaultRoute; the guard redirects here + search. */
  guardRedirect?: () => string;
  /** Today's `!loginStore.shouldBlockEditorXSignup()` wrapper — hidden when true. */
  hidden?: (root: RootStore) => boolean;
}

export const ROUTE_TABLE: RouteEntry[] = [
  { id: 'SSO_ENTER_EMAIL', path: ROUTES.SSO_ENTER_EMAIL, exact: true, component: SsoEnterEmail },
  { id: 'SSO_MAIN', path: ROUTES.SSO_MAIN, exact: true, component: ApproveAndContinue },
  {
    id: 'SSO_EMAIL_ALREADY_EXIST',
    path: ROUTES.SSO_EMAIL_ALREADY_EXIST,
    exact: true,
    component: EmailAlreadyExist,
    guard: (root) => root.ssoEmailAlreadyExistStore.enableAccess,
  },
  {
    id: 'SELECT_ACCOUNT',
    path: ROUTES.SELECT_ACCOUNT,
    exact: true,
    component: SelectAccount,
    guard: (root) => root.selectAccountStore.enableAccess(),
  },
  {
    id: 'SELECT_WORKSPACE',
    path: ROUTES.SELECT_WORKSPACE,
    exact: true,
    component: SelectWorkspace,
    guard: (root) => root.selectWorkspaceStore.enableAccess(),
  },
  { id: 'EMAIL_STEP', path: ROUTES.EMAIL_STEP, exact: true, component: EmailStepDialog },
  { id: 'LOGIN_PASSWORD', path: ROUTES.LOGIN_PASSWORD, exact: true, component: Login },
  {
    id: 'SOCIAL_ONLY',
    path: ROUTES.SOCIAL_ONLY,
    exact: true,
    component: SocialOnlyDialog,
    guard: (root) => root.socialAuthStore.enableAccess,
  },
  {
    id: 'SIGNUP_PASSWORD',
    path: ROUTES.SIGNUP_PASSWORD,
    exact: true,
    component: Signup,
    hidden: (root) => root.loginStore.shouldBlockEditorXSignup(),
  },
  {
    id: 'RESET_PASSWORD_CONFIRMATION',
    path: ROUTES.RESET_PASSWORD_CONFIRMATION,
    exact: true,
    component: ResetPasswordConfirmation,
    guard: (root) => root.resetPasswordStore.enableAccessConfirmation,
  },
  {
    id: 'RESET_PASSWORD',
    path: ROUTES.RESET_PASSWORD,
    exact: true,
    component: ResetPassword,
    guard: (root) => root.resetPasswordStore.enableAccess,
  },
  {
    id: 'RESET_PASSWORD_INVALID',
    path: ROUTES.RESET_PASSWORD_INVALID,
    exact: true,
    component: ResetPasswordInvalid,
    guard: (root) => root.resetPasswordInvalidStore.enableAccess,
  },
  {
    id: 'TWO_FACTOR_AUTH',
    path: ROUTES.TWO_FACTOR_AUTH,
    exact: true,
    component: TwoFactorAuthDialog,
    guard: (root) => root.twoFactorAuthStore.enableAccess(),
  },
  {
    id: 'POST_SIGNUP_EMAIL_CONFIRMATION',
    path: ROUTES.POST_SIGNUP_EMAIL_CONFIRMATION,
    exact: true,
    component: PostSignupEmailConfirm,
    guard: (root) => root.postSignupEmailConfirmStore.enableAccess(),
  },
  {
    id: 'RECOVER_ACCOUNT',
    path: ROUTES.RECOVER_ACCOUNT,
    exact: true,
    component: AccountRecoveryCodeDialog,
    guard: (root) => root.accountRecoveryCodeStore.enableAccess(),
  },
  {
    id: 'STEP_UP',
    path: ROUTES.STEP_UP,
    exact: true,
    component: StepUpDialog,
    guard: (root) => root.stepUpAuthStore.enableAccess(),
  },
  {
    id: 'ACCOUNT_VERIFICATION',
    path: ROUTES.ACCOUNT_VERIFICATION,
    exact: true,
    component: AccountVerification,
    guard: (root) => root.accountVerificationStore.enableAccess(),
  },
  { id: 'POST_LOGIN', path: ROUTES.POST_LOGIN, component: PostLoginRouter },
  {
    id: 'GOOGLE_LOGIN_OR_STEP_UP_INITIATION',
    path: [ROUTES.GOOGLE_LOGIN, ROUTES.STEP_UP_INITIATION],
    component: LoadingDialog,
  },
  {
    id: 'SOCIAL_ENTER_EMAIL',
    path: ROUTES.SOCIAL_ENTER_EMAIL,
    exact: true,
    component: EnterEmailForSocial,
    guard: (root) => root.postSocialLoginEnterEmailStore.enableAccess,
  },
  {
    id: 'DEVICE_LOGIN',
    path: ROUTES.DEVICE_LOGIN,
    exact: true,
    component: DeviceLogin,
    guard: (root) => root.deviceLoginStore.enableAccess(),
    guardRedirect: () =>
      `/?redirectTo=${window.location.origin}/login${ROUTES.DEVICE_LOGIN}`,
  },
  { id: 'DEVICE_AUTHORIZE', path: ROUTES.DEVICE_AUTHORIZE, exact: true, component: DeviceAuthorize },
  {
    id: 'POS_DEVICE_LOGIN',
    path: ROUTES.POS_DEVICE_LOGIN,
    exact: true,
    component: PosDeviceLogin,
    guard: (root) => root.posDeviceLoginStore.enableAccess(),
    guardRedirect: () =>
      `/?targetUrl=${window.location.origin}/login${ROUTES.POS_DEVICE_LOGIN}`,
  },
  {
    id: 'CONNECTION_SUCCESSFUL',
    path: ROUTES.CONNECTION_SUCCESSFUL,
    exact: true,
    component: ConnectionSuccessful,
  },
  {
    id: 'POS_DEVICE_CONNECTION_SUCCESSFUL',
    path: ROUTES.POS_DEVICE_CONNECTION_SUCCESSFUL,
    exact: true,
    component: PosDeviceConnectionSuccessful,
  },
  {
    id: 'WEB5_DEVICE_LOGIN',
    path: ROUTES.WEB5_DEVICE_LOGIN,
    exact: true,
    component: Web5DeviceLogin,
    guard: (root) => root.web5DeviceLoginStore.enableAccess(),
    guardRedirect: () =>
      `/?targetUrl=${window.location.origin}/login${ROUTES.WEB5_DEVICE_LOGIN}`,
  },
  {
    id: 'WEB5_DEVICE_CONNECTION_SUCCESSFUL',
    path: ROUTES.WEB5_DEVICE_CONNECTION_SUCCESSFUL,
    exact: true,
    component: Web5DeviceConnectionSuccessful,
  },
  {
    id: 'DISCONNECT_SUCCESSFUL',
    path: ROUTES.DISCONNECT_SUCCESSFUL,
    exact: true,
    component: DisconnectSuccessful,
  },
  { id: 'BLOCKED_ACCOUNT', path: ROUTES.BLOCKED_ACCOUNT, exact: true, component: BlockedAccount },
  {
    id: 'EMAIL_VERIFICATION_RESULT',
    path: ROUTES.EMAIL_VERIFICATION_RESULT,
    exact: true,
    component: EmailVerificationResult,
    guard: (root) => root.emailVerificationResultStore.enableAccess,
  },
];

/** The trailing `<Redirect to={`${DEFAULT_ROUTE}${search}`} />` fallback. */
export const DEFAULT_REDIRECT = DEFAULT_ROUTE;
